import { PerformanceGranularity } from '@codezo/graphql';
import { useState } from 'react';
import { cn } from '../../../lib/utils';
import {
	type Bucket,
	type PerformanceStats,
	bucketLabel,
	bucketName,
	compactMoney,
	cumulative,
	money,
	niceTop,
	projectMonth,
	rangeLabel,
} from './performanceFormat';

const CHART_H = 'h-[300px]';
const PLOT_INSET = 'ml-12 mr-10';

function GridLines({ top, currency }: { top: number; currency: string }) {
	return (
		<>
			{[0, 0.5, 1].map((r) => (
				<div
					key={r}
					className="absolute right-0 -left-12 flex translate-y-1/2 items-center gap-2.5"
					style={{ bottom: `${r * 100}%` }}
				>
					<span className="w-[38px] text-right font-mono text-2xs text-text-4">
						{compactMoney(top * r, currency)}
					</span>
					<span className="h-px flex-1 bg-line-1" />
				</div>
			))}
		</>
	);
}

function Tip({ bottom, children }: { bottom: string; children: React.ReactNode }) {
	return (
		<span
			className="pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 rounded-md bg-surface-raised px-2 py-1 text-xs whitespace-nowrap text-text-1 tabular-nums shadow-md ring-1 ring-line-2"
			style={{ bottom: `calc(${bottom} + 12px)` }}
		>
			{children}
		</span>
	);
}

function AxisLabels({ labels, todayIndex }: { labels: string[]; todayIndex: number }) {
	return (
		<div className={cn('mt-2.5 mb-4 flex', PLOT_INSET)}>
			{labels.map((l, i) => (
				<span
					// eslint-disable-next-line react/no-array-index-key
					key={i}
					className={cn(
						'min-w-0 flex-1 text-center font-mono text-2xs whitespace-nowrap',
						i === todayIndex
							? 'text-text-1'
							: i > todayIndex && todayIndex >= 0
								? 'text-line-3'
								: 'text-text-4'
					)}
				>
					{l}
				</span>
			))}
		</div>
	);
}

const todayIndexOf = (buckets: Bucket[]) => {
	const firstFuture = buckets.findIndex((b) => b.future);
	return firstFuture === -1 ? buckets.length - 1 : firstFuture - 1;
};

/* ── Month: cumulative revenue against the comparison month, with a run-rate projection ── */

export function MonthChart({ stats }: { stats: PerformanceStats }) {
	const [hover, setHover] = useState<number | null>(null);
	const { buckets, currency } = stats;
	const prev = stats.comparisonBuckets ?? null;
	const n = buckets.length;
	const todayIndex = todayIndexOf(buckets);
	const cum = cumulative(buckets);
	const cumToday = cumulative(buckets)[todayIndexOf(buckets)] ?? 0;
	// Nothing accepted yet means nothing to project from.
	const projection = cumToday > 0 ? projectMonth(buckets) : null;
	const prevCum = prev ? cumulative(prev) : null;
	const top = niceTop(
		Math.max(projection?.[n - 1] ?? 0, cum[todayIndex] ?? 0, prevCum?.[prevCum.length - 1] ?? 0)
	);

	const x = (i: number) => ((i + 0.5) / n) * 1000;
	const y = (v: number) => 100 - (v / top) * 100;
	const path = (vals: number[], from = 0) =>
		vals
			.map((v, k) => `${k ? 'L' : 'M'}${x(from + k).toFixed(1)} ${y(v).toFixed(2)}`)
			.join(' ');
	const pctX = (i: number) => `${((i + 0.5) / n) * 100}%`;
	const pctY = (v: number) => `${(v / top) * 100}%`;

	const actual = cum.slice(0, todayIndex + 1);
	const actualPath = path(actual);
	const area = `${actualPath} L${x(todayIndex).toFixed(1)} 100 L${x(0).toFixed(1)} 100 Z`;
	const projPath = projection ? path(projection.slice(todayIndex), todayIndex) : '';
	// Last month can be shorter; its line simply ends at its last day.
	const prevPath = prevCum ? path(prevCum.slice(0, n)) : '';
	const prevName = prev?.[0]
		? new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long' }).format(
				new Date(prev[0].start)
			)
		: '';
	const nowName = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long' }).format(
		new Date(buckets[0].start)
	);
	const prevTotal = prevCum?.[prevCum.length - 1];
	const projected = projection?.[n - 1];
	const labels = buckets.map((b, i) =>
		bucketLabel(b, i, buckets, PerformanceGranularity.Day, todayIndex)
	);

	return (
		<>
			<div className="mr-10 ml-12 flex flex-wrap items-center gap-x-3.5 gap-y-1 pb-4 text-xs text-text-3">
				<LegendLine solid label={nowName} />
				{prev && <LegendLine dashed label={prevName} />}
				{projected != null && (
					<span className="inline-flex items-center gap-1.5">
						<svg width="16" height="8" viewBox="0 0 16 8" aria-hidden="true">
							<path
								d="M1 4h14"
								stroke="var(--color-accent)"
								strokeWidth="2"
								strokeDasharray="1 3"
								strokeLinecap="round"
							/>
						</svg>
						Projected{' '}
						<span className="text-text-2 tabular-nums">
							{compactMoney(projected, currency)}
						</span>
					</span>
				)}
			</div>
			<div
				className={cn('relative mt-3.5', CHART_H, PLOT_INSET)}
				onMouseLeave={() => setHover(null)}
			>
				<GridLines top={top} currency={currency} />
				<svg
					aria-hidden="true"
					viewBox="0 0 1000 100"
					preserveAspectRatio="none"
					className="pointer-events-none absolute inset-0 size-full overflow-visible"
				>
					<path d={area} fill="var(--color-accent)" fillOpacity="0.12" />
					{prevPath && (
						<path
							d={prevPath}
							fill="none"
							stroke="var(--color-text-3)"
							strokeWidth="1.5"
							strokeDasharray="4 4"
							vectorEffect="non-scaling-stroke"
						/>
					)}
					{projPath && (
						<path
							d={projPath}
							fill="none"
							stroke="var(--color-accent)"
							strokeWidth="2"
							strokeDasharray="1 5"
							strokeLinecap="round"
							vectorEffect="non-scaling-stroke"
						/>
					)}
					<path
						d={actualPath}
						fill="none"
						stroke="var(--color-accent)"
						strokeWidth="2"
						strokeLinejoin="round"
						vectorEffect="non-scaling-stroke"
					/>
				</svg>
				<span
					className="absolute -top-1.5 bottom-0 w-0 border-l border-dashed border-line-3"
					style={{ left: pctX(todayIndex) }}
				/>
				<span
					className="absolute -top-[22px] -translate-x-1/2 rounded-[5px] border border-accent-line bg-accent-soft px-1.5 py-0.5 text-2xs font-medium whitespace-nowrap text-accent"
					style={{ left: pctX(todayIndex) }}
				>
					Today
				</span>
				<span
					className="absolute z-[2] -mb-[4.5px] -ml-[4.5px] size-[9px] rounded-full bg-accent shadow-[0_0_0_3px_var(--color-surface)]"
					style={{ left: pctX(todayIndex), bottom: pctY(cum[todayIndex] ?? 0) }}
				/>
				{projected != null && todayIndex < n - 1 && (
					<span
						className="absolute right-0 rounded bg-surface px-1 text-right text-xs whitespace-nowrap text-accent tabular-nums"
						style={{ bottom: `calc(${pctY(projected)} + 10px)` }}
					>
						Projected {money(projected, currency)}
					</span>
				)}
				{prevTotal != null && (
					<span
						className="absolute right-0 rounded bg-surface px-1 text-right text-xs whitespace-nowrap text-text-3 tabular-nums"
						style={{ bottom: `calc(${pctY(prevTotal)} - 22px)` }}
					>
						{prevName} {money(prevTotal, currency)}
					</span>
				)}
				<div className="absolute inset-0 flex">
					{buckets.map((b, i) => {
						const v = projection?.[i] ?? cum[i];
						const future = i > todayIndex;
						return (
							<div
								key={b.start}
								className="relative h-full min-w-0 flex-1"
								onMouseEnter={() => setHover(i)}
							>
								{hover === i && (
									<>
										<span className="absolute inset-y-0 left-1/2 w-px bg-line-2" />
										<span
											className="absolute left-1/2 z-[3] -mb-[3.5px] -ml-[3.5px] size-[7px] rounded-full bg-text-1"
											style={{ bottom: pctY(v) }}
										/>
										<Tip bottom={pctY(v)}>
											{bucketName(b, PerformanceGranularity.Day)}:{' '}
											{money(v, currency)}
											{future ? ' projected' : ''}
											{prevCum && prev?.[i] && (
												<span className="text-text-3">
													{' '}
													·{' '}
													{bucketName(
														prev[i],
														PerformanceGranularity.Day
													)}
													: {money(prevCum[i], currency)}
												</span>
											)}
										</Tip>
									</>
								)}
							</div>
						);
					})}
				</div>
			</div>
			<AxisLabels labels={labels} todayIndex={todayIndex} />
		</>
	);
}

function LegendLine({
	label,
	solid,
	dashed,
}: {
	label: string;
	solid?: boolean;
	dashed?: boolean;
}) {
	return (
		<span className="inline-flex items-center gap-1.5">
			<svg width="16" height="8" viewBox="0 0 16 8" aria-hidden="true">
				<path
					d="M1 4h14"
					stroke={solid ? 'var(--color-accent)' : 'var(--color-text-3)'}
					strokeWidth={solid ? 2 : 1.5}
					strokeDasharray={dashed ? '3 2.5' : undefined}
				/>
			</svg>
			{label}
		</span>
	);
}

/* ── Weeks, months, years: revenue bars with the margin line on its own axis ── */

export function BarChart({ stats }: { stats: PerformanceStats }) {
	const [hover, setHover] = useState<number | null>(null);
	const { buckets, currency, granularity } = stats;
	const n = buckets.length;
	const todayIndex = todayIndexOf(buckets);
	const top = niceTop(Math.max(...buckets.map((b) => b.revenue), 0));
	const margins = buckets.map((b) => b.marginPercent).filter((m): m is number => m != null);
	const mLo = margins.length ? Math.floor((Math.min(...margins) - 5) / 10) * 10 : 0;
	const mHi = margins.length ? Math.ceil((Math.max(...margins) + 5) / 10) * 10 : 50;
	const mY = (m: number) => ((m - mLo) / (mHi - mLo)) * 100;
	// A margin on a sliver of revenue (a part month, one small order) is noise: drawn faded and
	// dashed, so the line's shape comes from the months that carry real volume.
	const maxRevenue = Math.max(...buckets.map((b) => b.revenue), 0);
	const thin = (b: Bucket) => b.revenue > 0 && b.revenue < maxRevenue * 0.1;
	const points = buckets
		.map((b, i) =>
			b.marginPercent == null
				? null
				: { i, x: ((i + 0.5) / n) * 1000, y: 100 - mY(b.marginPercent), thin: thin(b) }
		)
		.filter((p): p is { i: number; x: number; y: number; thin: boolean } => p !== null);
	const segments = points
		.slice(1)
		.map((p, k) => ({ a: points[k], b: p, faint: p.thin || points[k].thin }));
	// Values on the dots instead of a second axis; past a year of bars they would crowd, so the tooltip carries them.
	const labelDots = n <= 13;
	const gap = n > 20 ? 'px-[1.5px]' : n > 10 ? 'px-1' : 'px-2.5';
	const labels = buckets.map((b, i) => bucketLabel(b, i, buckets, granularity, todayIndex));

	return (
		<>
			<div className="mr-10 ml-12 flex flex-wrap items-center gap-x-3.5 gap-y-1 pb-4 text-xs text-text-3">
				<span className="inline-flex items-center gap-1.5">
					<span className="size-2 rounded-[2px] bg-accent" />
					Revenue
				</span>
				<span className="inline-flex items-center gap-1.5">
					<svg width="16" height="8" viewBox="0 0 16 8" aria-hidden="true">
						<path d="M1 4h14" stroke="var(--color-text-1)" strokeWidth="1.5" />
						<circle
							cx="8"
							cy="4"
							r="2.5"
							fill="var(--color-surface)"
							stroke="var(--color-text-1)"
							strokeWidth="1.5"
						/>
					</svg>
					Gross margin
				</span>
			</div>
			<div
				className={cn('relative mt-3.5', CHART_H, PLOT_INSET)}
				onMouseLeave={() => setHover(null)}
			>
				<GridLines top={top} currency={currency} />
				<div className="absolute inset-0 flex items-end">
					{buckets.map((b, i) => {
						const empty = !b.future && b.revenue === 0;
						const h = b.future || empty ? 1.2 : (b.revenue / top) * 100;
						const my = b.marginPercent == null ? 0 : mY(b.marginPercent);
						const strong = i === todayIndex || hover === i;
						return (
							<div
								key={b.start}
								className={cn(
									'relative flex h-full min-w-0 flex-1 flex-col justify-end',
									gap
								)}
								onMouseEnter={() => setHover(i)}
							>
								<span
									className={cn(
										'block rounded-t-[3px] rounded-b-[1px] transition-opacity',
										b.future ? 'bg-line-1' : empty ? 'bg-line-2' : 'bg-accent',
										!b.future && !empty && !strong && 'opacity-35'
									)}
									style={{ height: `${h}%` }}
								/>
								{b.marginPercent != null && (
									<>
										<span
											className={cn(
												'absolute left-1/2 z-[2] -mb-[3.5px] -ml-[3.5px] size-[7px] rounded-full bg-surface shadow-[0_0_0_1.5px_var(--color-text-1)]',
												thin(b) && 'opacity-40'
											)}
											style={{ bottom: `${my}%` }}
										/>
										{labelDots && hover !== i && (
											<span
												className={cn(
													'absolute left-1/2 z-[2] -translate-x-1/2 rounded bg-surface/80 px-1 font-mono text-2xs whitespace-nowrap',
													thin(b) ? 'text-text-4' : 'text-text-2'
												)}
												style={{ bottom: `calc(${my}% + 8px)` }}
											>
												{b.marginPercent.toFixed(1)}%
											</span>
										)}
									</>
								)}
								{hover === i && (
									<Tip bottom={`${Math.max(h, my)}%`}>
										{bucketName(b, granularity)}:{' '}
										{b.future
											? 'upcoming'
											: empty
												? 'no orders'
												: `${money(b.revenue, currency)}${i === todayIndex ? ' so far' : ''}`}
										{b.marginPercent != null && (
											<span className="text-text-3">
												{' '}
												· {b.marginPercent.toFixed(1)}% margin
												{thin(b) ? ' (small base)' : ''}
											</span>
										)}
									</Tip>
								)}
							</div>
						);
					})}
				</div>
				<svg
					aria-hidden="true"
					viewBox="0 0 1000 100"
					preserveAspectRatio="none"
					className="pointer-events-none absolute inset-0 z-[1] size-full overflow-visible"
				>
					{segments.map(({ a, b, faint }) => (
						<line
							key={a.i}
							x1={a.x}
							y1={a.y}
							x2={b.x}
							y2={b.y}
							stroke="var(--color-text-1)"
							strokeOpacity={faint ? 0.3 : 0.75}
							strokeWidth="1.5"
							strokeDasharray={faint ? '3 3' : undefined}
							vectorEffect="non-scaling-stroke"
						/>
					))}
				</svg>
			</div>
			<AxisLabels labels={labels} todayIndex={todayIndex} />
		</>
	);
}

export function chartUnit(stats: PerformanceStats): string {
	return rangeLabel(stats.range.start, stats.range.end);
}
